import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { ToasterService } from '../services'
import { roundToTwo, isInMarketAllowlist } from '../utils'
import { ProgressBarGrowDirection } from '../types'
import { SbCartPresentsNotificationComponent } from './sb-cart-presents-notification.component'
import type { ProgressBar } from '../types'
import { SbSidebarComponent } from '.'

interface Scope {
  // OPTIONS
  progressBars: ProgressBar[]
  /** The total price of all of the items in the cart in the currency's subunit, after discounts have been applied. */
  cartTotalPrice: number
  /** The total price of all of the items in the cart in the currency's subunit, before discounts have been applied. */
  cartOriginalTotalPrice: number
  assetsPath: string
  /** The next progress bar which is not reached yet (warning / yellow toaster) */
  active?: ProgressBar
  /** The last progress bar which is reached (success / green toaster) */
  reached?: ProgressBar

  // METHODS called by the template
  setActive: SbCartProgressBarsComponent['setActive']
  setReached: SbCartProgressBarsComponent['setReached']

  // TEMPLATES
}

export class SbCartProgressBarsComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-template foobar="hello">...</sb-template>`
   */
  public static tagName = 'sb-cart-progress-bars'

  protected toasterService = ToasterService.getSingleton()

  public fullReady = false

  public scope: Scope = {
    // OPTIONS
    progressBars: [],
    cartTotalPrice: 0,
    cartOriginalTotalPrice: 0,
    assetsPath: window.assetsPath,
    active: undefined,
    reached: undefined,

    // METHODS
    setActive: this.setActive.bind(this),
    setReached: this.setReached.bind(this),

    // TEMPLATES
  }

  private static readonly activeProgressBarToastId = Symbol(
    'SbCartProgressBars.active',
  )
  private static readonly reachedProgressBarToastId = Symbol(
    'SbCartProgressBars.reached',
  )

  sbCartPresentsNotificationComponents: SbCartPresentsNotificationComponent[] =
    []

  static get observedAttributes(): string[] {
    return [
      'assets-path',
      'cart-original-total-price',
      'cart-total-price',
      'progress-bars',
    ]
  }

  static getProgressBarToastId(progressBar: ProgressBar) {
    return `${progressBar.fromTotal}.${progressBar.type}.${progressBar.labelReached}`
  }

  /**
   * Called as soon as this custom element es detected in the DOM
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCartProgressBarsComponent.observedAttributes)
  }

  protected parsedAttributeChangedCallback(attributeName: string) {
    if (
      attributeName === 'cartOriginalTotalPrice' ||
      attributeName === 'cartTotalPrice'
    ) {
      if (this.fullReady) {
        this.recalculate()
      }
    }
  }

  private toastTimeout: number | undefined

  private sidebarEl: SbSidebarComponent | undefined

  /**
   * Recalculate the progress bars
   */
  protected recalculate() {
    this.calcDiff()
    this.setReached(this.getLatestReached())
    this.setActive(this.getNextReachable())

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout)
    }

    /*
    Dirty workaround: The toast should only be displayed when the cart is visible.
    When adding a product to the (closed) cart the cart opens and the toast should be displayed.
    However in this case the opening happens after the attribute update. That's why we need to wait a bit before showing the toasts.
    */
    this.toastTimeout = setTimeout(
      () => {
        this.processReachedToast()
        this.processActiveToast()
      },
      this.isVisible ? 0 : 500,
    )
  }

  private get isVisible() {
    return this.sidebarEl?.getState() !== 'hidden'
  }

  private processActiveToast() {
    this.toasterService.removeById(
      SbCartProgressBarsComponent.activeProgressBarToastId,
    )

    if (!this.isVisible) {
      return
    }

    if (!this.scope.active || !this.scope.cartTotalPrice) {
      return
    }

    const message = this.scope.active.label || ''
    if (!message) {
      return
    }

    this.toasterService.warning(
      message,
      // The message of the toast uses the `active` scope, so we need to set the active progress bar as active
      {
        active: this.scope.active,
      },
      {
        id: SbCartProgressBarsComponent.activeProgressBarToastId,
      },
    )
  }

  private processReachedToast() {
    this.toasterService.removeById(
      SbCartProgressBarsComponent.reachedProgressBarToastId,
    )

    if (!this.isVisible) {
      return
    }

    if (!this.scope.reached || !this.scope.cartTotalPrice) {
      return
    }

    // Do not show success toaster for presents because the popup will show the present notification
    if (this.scope.reached.type === 'free-present') {
      return
    }

    const message =
      this.scope.reached.labelReached || this.scope.reached.label || ''
    if (!message) {
      return
    }

    this.toasterService.success(
      message,
      // The message of the toast uses the `active` scope, so we need to set the active progress bar as active
      {
        active: this.scope.reached,
      },
      {
        id: SbCartProgressBarsComponent.reachedProgressBarToastId,
      },
    )
  }

  /**
   * Get all `SbCartPresentsNotificationComponents` as soon as they are ready
   * @returns All SbCartPresentsNotificationComponents as soon as they are ready
   */
  protected async getSbCartPresentsNotificationComponents() {
    const components = Array.from(
      document.querySelectorAll<SbCartPresentsNotificationComponent>(
        SbCartPresentsNotificationComponent.tagName,
      ),
    )

    // Wait until all SbCartPresentsNotificationComponents are ready
    return new Promise<typeof components>(resolve => {
      const checkReady = () => {
        const allReady = components.every(component => component.fullReady)
        if (allReady) {
          resolve(components)
        } else {
          setTimeout(checkReady, 1000) // Retry after 100ms
        }
      }
      checkReady()
    })
  }

  /**
   * Prepare the progress bars
   */
  protected async prepare() {
    this.sbCartPresentsNotificationComponents =
      await this.getSbCartPresentsNotificationComponents()
    this.filter()
    this.sort()
    this.setProps()
    this.calcWidth()
  }

  /**
   * Set the active progress bar
   * @param progressBar The progress bar to set as active
   */
  public setActive(progressBar?: ProgressBar) {
    this.scope.active = progressBar
  }

  public setReached(
    progressBar?: ProgressBar,
    _direction?: ProgressBarGrowDirection,
  ) {
    // Nothing has changed
    if (this.scope.reached === progressBar) {
      return
    }

    if (!progressBar?.reached) {
      this.scope.reached = undefined
      return
    }

    this.scope.reached = progressBar
  }

  public getNextReachable() {
    for (const progressBar of this.scope.progressBars) {
      if (!progressBar.reached) {
        return progressBar
      }
    }

    return undefined
  }

  public getLatestReached() {
    // loop backwards
    for (let i = this.scope.progressBars.length - 1; i >= 0; i--) {
      const progressBar = this.scope.progressBars[i]
      if (progressBar.reached) {
        return progressBar
      }
    }

    return undefined
  }

  /**
   * Sort progress bars by `fromTotal`
   */
  protected sort() {
    this.scope.progressBars.sort((a, b) => {
      return a.fromTotal - b.fromTotal
    })
  }

  /**
   * Remove empty and not available (for the current market) progress bars
   */
  protected filter() {
    this.scope.progressBars = this.scope.progressBars.filter(progressBar => {
      return (
        Object.keys(progressBar).length > 0 &&
        progressBar.fromTotal > 0 &&
        isInMarketAllowlist(progressBar.markets)
      )
    })
  }

  /**
   * Calculate the width of each progress bar in the row (used for the css width property)
   */
  protected calcWidth() {
    const startPrice = 0
    const endPrice = this.getEndPrice()
    let sumWidth = 0

    for (const progressBar of this.scope.progressBars) {
      progressBar.width = roundToTwo(
        ((progressBar.fromTotal - startPrice) / (endPrice - startPrice)) * 100 -
          sumWidth,
      )
      sumWidth += progressBar.width
    }
  }

  /**
   * Get the highest `fromTotal` value of all progress bars
   * @returns The highest `fromTotal` value of all progress bars
   */
  protected getEndPrice() {
    let endPrice = 0
    for (const progressBar of this.scope.progressBars) {
      endPrice = Math.max(endPrice, progressBar.fromTotal)
    }
    return endPrice
  }

  protected findPresentObject(progressBar: ProgressBar) {
    if (progressBar.type !== 'free-present') {
      return undefined
    }

    const presents = this.sbCartPresentsNotificationComponents
      .map(component => component.getPresents())
      .flat()

    const present = presents.find(
      present => present.fromTotal === progressBar.fromTotal,
    )

    return present
  }

  /**
   * Set missing progress bar properties
   */
  protected setProps() {
    for (let i = 0; i < this.scope.progressBars.length; i++) {
      const progressBar = this.scope.progressBars[i]

      // Set previous and next reference
      if (!progressBar.previous)
        progressBar.previous = this.scope.progressBars[i - 1]
      if (!progressBar.next) progressBar.next = this.scope.progressBars[i + 1]

      if (progressBar.reached === undefined) progressBar.reached = false

      // Find present for progress bar
      if (progressBar.type === 'free-present') {
        progressBar.present = this.findPresentObject(progressBar)
        if (!progressBar.present) {
          console.warn('No present found for progress bar', progressBar)
        }
      }
    }
  }

  /**
   * Calculate the diff and progress of each progress bar based on the `cart.totalPrice`
   */
  protected calcDiff() {
    const total = this.scope.cartTotalPrice // this.scope.cartOriginalTotalPrice
    for (const progressBar of this.scope.progressBars) {
      progressBar.diff = progressBar.fromTotal - total
      if (progressBar.diff < 0) {
        progressBar.diff = 0
      }

      progressBar.reached = progressBar.fromTotal <= total

      const startPrice = progressBar.previous?.fromTotal || 0

      if (progressBar.reached) {
        progressBar.progress = 100
      } else if (
        progressBar.previous &&
        progressBar.previous.reached === false
      ) {
        progressBar.progress = 0
      } else {
        // Calc progress in percent
        progressBar.progress = roundToTwo(
          100 - (progressBar.diff / (progressBar.fromTotal - startPrice)) * 100,
        )

        if (progressBar.progress < 0) {
          progressBar.progress = 0
        }
        if (progressBar.progress > 100) {
          progressBar.progress = 100
        }
      }
    }
  }

  async beforeBind() {
    await this.prepare()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    if (this.scope.cartTotalPrice === undefined) {
      this.scope.cartTotalPrice = 0
    }
    if (this.scope.cartOriginalTotalPrice === undefined) {
      this.scope.cartOriginalTotalPrice = 0
    }
    this.recalculate()
    this.fullReady = true
    this.classList.add(`${SbCartProgressBarsComponent.tagName}-ready`)
    this.sidebarEl = this.closest<SbSidebarComponent>('sb-sidebar') || undefined
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <>
          <div class="sb-cart-progress-bar-row">
            <div
              class="sb-cart-progress-bar-container"
              rv-class-reached="bar.reached"
              rv-each-bar="progressBars"
              rv-style-width="bar.width | append '%'"
            >
              <button
                type="button"
                class="sb-cart-progress-bar-marker"
                rv-on-click="setActive | args bar 'byIndex'"
              >
                <sb-icon rv-src="bar.icon" size={20}></sb-icon>
              </button>
              <div class="sb-cart-progress-bar">
                <div
                  class="sb-cart-progress-bar-fill"
                  style="width: 0%"
                  rv-style-width="bar.progress | append '%'"
                ></div>
              </div>
            </div>
          </div>
          <div class="w-full text-sm mb-3">
            <p
              rv-hide="active.reached"
              rv-template="active.label | default ' '"
            ></p>
            <p
              rv-show="active.reached"
              rv-template="active.labelReached | default active.label | default ' '"
            ></p>
          </div>
        </>
      )
    }
    return null
  }
}
